import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Gift, ArrowRight, Clock, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";

const SellExitIntent = () => {
  const [show, setShow] = useState(false);
  const triggered = useRef(false);
  const dismissed = useRef(false);

  useEffect(() => {
    // Only trigger once per session
    const alreadyShown = sessionStorage.getItem("exit-intent-shown");
    if (alreadyShown) return;

    // Desktop only: mouseleave detection
    const isMobile = window.matchMedia("(max-width: 767px)").matches || "ontouchstart" in window;
    if (isMobile) return;

    const handleMouseLeave = (e: MouseEvent) => {
      if (
        e.clientY <= 5 &&
        !triggered.current &&
        !dismissed.current &&
        window.scrollY > 400
      ) {
        triggered.current = true;
        sessionStorage.setItem("exit-intent-shown", "true");
        setShow(true);
      }
    };

    document.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      document.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, []);

  const handleDismiss = () => {
    dismissed.current = true;
    setShow(false);
  };

  return (
    <AnimatePresence>
      {show && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[100]"
            onClick={handleDismiss}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[101] w-[90vw] max-w-lg"
          >
            <div className="relative p-6 sm:p-8 md:p-10 rounded-3xl bg-card border border-primary/20 shadow-2xl shadow-primary/10 overflow-hidden">
              {/* Background glow */}
              <div className="absolute top-0 right-0 w-40 h-40 bg-primary/10 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-40 h-40 bg-accent/10 rounded-full blur-3xl" />

              {/* Close button */}
              <button
                onClick={handleDismiss}
                className="absolute top-4 right-4 p-2 rounded-full hover:bg-secondary/80 transition-colors text-muted-foreground hover:text-foreground"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="relative text-center">
                {/* Icon */}
                <motion.div
                  animate={{ rotate: [0, -10, 10, -10, 0] }}
                  transition={{ repeat: Infinity, duration: 2, delay: 0.5 }}
                  className="w-16 h-16 rounded-2xl bg-gradient-to-r from-primary to-accent flex items-center justify-center mx-auto mb-6 shadow-lg shadow-primary/25"
                >
                  <Gift className="w-8 h-8 text-primary-foreground" />
                </motion.div>

                <h3 className="text-xl sm:text-2xl md:text-3xl font-extrabold mb-3">
                  Attendez !
                </h3>
                <p className="text-base sm:text-lg text-muted-foreground mb-2">
                  On vous offre un{" "}
                  <strong className="text-foreground">échange gratuit de 15 minutes</strong>
                </p>
                <p className="text-sm text-muted-foreground mb-6">
                  Discutons de votre projet sans engagement. On vous conseille sur la meilleure formule pour votre métier.
                </p>

                {/* Trust signals */}
                <div className="flex flex-wrap justify-center gap-3 mb-6 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5 text-primary" />
                    15 min max
                  </span>
                  <span className="flex items-center gap-1">
                    <ShieldCheck className="w-3.5 h-3.5 text-primary" />
                    Sans engagement
                  </span>
                  <span className="flex items-center gap-1">
                    <Gift className="w-3.5 h-3.5 text-primary" />
                    100% gratuit
                  </span>
                </div>

                <div className="flex flex-col gap-3">
                  <a href="#contact" onClick={handleDismiss}>
                    <Button size="lg" className="w-full bg-gradient-to-r from-primary to-accent hover:opacity-90 shadow-lg shadow-primary/25 group py-6 text-base">
                      Réserver mon échange gratuit
                      <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </a>
                  <button
                    onClick={handleDismiss}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors py-2"
                  >
                    Non merci, je continue ma visite
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SellExitIntent;
